// =============================================================================
// AssetRegisterScreen — /assets/insurance-register — the CapEx INSURANCE
// register (mig 351, James's CapEx ticket, 4 Sep).
//
// NOT the whole asset system: vehicles / power tools / hand tools / rigging
// live on the existing Assets screen at /assets (James, 4 Sep smoke: "there
// are more assets than just large CapEx insured ones"). This screen is the
// insurance slice only — significant purchases the broker must know about.
//
// "What do we own? → What is it worth? → Does it require individual
// insurance? → Has it actually been insured?"
//
// Fed automatically from AP coding (CapEx tool/equipment at/over the
// configurable threshold offers "Add to asset register" on approval) and by
// the manual Add button here. Anything insurance_status = pending sits in an
// amber banner until someone confirms it with the broker — the requirement
// can't disappear after the invoice is coded. Vehicles keep their own process.
// =============================================================================

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, CheckCircle2, Package, Plus, Search, ShieldCheck, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Button, Pill, Spinner, EmptyState, KPI } from "@/components/ui";
import { format, parseISO } from "date-fns";

interface AssetRow {
  asset_id: string;
  description: string;
  purchase_date: string | null;
  purchase_value: number | null;
  supplier_name: string | null;
  receipt_ref: string | null;
  budget_code: string | null;
  serial_number: string | null;
  location: string | null;
  insurance_required: boolean;
  insurance_status: "pending" | "insured" | "not_required";
  insurance_confirmed_on: string | null;
  notes: string | null;
  created_at: string;
}

const fmtMoney = (n: number | null) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function AssetRegisterScreen() {
  const user = useRequireAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "pending" | "insured">("all");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<AssetRow | null>(null);

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ["company_assets"],
    queryFn: async (): Promise<AssetRow[]> => {
      const { data, error } = await (supabase as any)
        .from("company_assets")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as AssetRow[];
    },
  });

  const { data: threshold = 5000 } = useQuery({
    queryKey: ["asset_threshold_display"],
    queryFn: async () => {
      const { data } = await (supabase as any).from("finance_settings")
        .select("value").eq("key", "asset_insurance_threshold").maybeSingle();
      return Number((data as any)?.value ?? 5000) || 5000;
    },
  });

  if (!user) return null;
  if (!hasRole(user, "manager")) {
    return <div className="p-6"><EmptyState icon={Package} title="Managers only" description="The asset register is a finance surface." /></div>;
  }

  const pending = assets.filter(a => a.insurance_status === "pending" && a.insurance_required);
  const insuredValue = assets.filter(a => a.insurance_status === "insured")
    .reduce((t, a) => t + Number(a.purchase_value ?? 0), 0);
  const totalValue = assets.reduce((t, a) => t + Number(a.purchase_value ?? 0), 0);

  const q = search.trim().toLowerCase();
  const shown = assets.filter(a => {
    if (tab === "pending" && !(a.insurance_status === "pending" && a.insurance_required)) return false;
    if (tab === "insured" && a.insurance_status !== "insured") return false;
    if (!q) return true;
    return [a.description, a.supplier_name, a.serial_number, a.location, a.budget_code, a.receipt_ref]
      .some(x => (x ?? "").toLowerCase().includes(q));
  });

  async function setStatus(a: AssetRow, status: AssetRow["insurance_status"]) {
    const { error } = await (supabase as any).from("company_assets").update({
      insurance_status: status,
      insurance_confirmed_on: status === "insured" ? new Date().toISOString().slice(0, 10) : null,
      insurance_required: status === "not_required" ? false : a.insurance_required,
      updated_by: (user as any).user_id ?? null,
    }).eq("asset_id", a.asset_id);
    if (error) { alert(error.message); return; }
    qc.invalidateQueries({ queryKey: ["company_assets"] });
  }

  return (
    <div className="p-4 md:p-6 max-w-[1100px] mx-auto">
      <div className="mb-5 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="h-1 w-10 bg-accent rounded-full" />
            <div className="text-text-muted text-[11px] tracking-wider2 uppercase font-mono">
              Tools, plant &amp; equipment · flagged from AP coding at ${threshold.toLocaleString()}+
            </div>
          </div>
          <h1 className="text-[24px] font-semibold tracking-tight">Insurance register</h1>
          <div className="text-text-muted text-[12px] mt-1">
            Significant purchases the broker must cover individually. Company policies
            live at{" "}
            <Link to="/company-compliance" className="text-accent hover:underline">Company compliance</Link>;
            the full asset registers — vehicles, tools, rigging — at{" "}
            <Link to="/assets" className="text-accent hover:underline">Assets</Link>.
          </div>
        </div>
        <Button size="sm" icon={Plus} onClick={() => setAdding(true)}>Add asset</Button>
      </div>

      {/* the point of the whole ticket: outstanding insurance can't hide */}
      {pending.length > 0 && (
        <Card className="mb-4 border-warn/40">
          <div className="p-4 flex items-center gap-3">
            <AlertTriangle size={18} className="text-warn shrink-0" />
            <div className="flex-1 min-w-0 text-[13px] text-text">
              <b>{pending.length} asset{pending.length === 1 ? "" : "s"}</b> waiting on insurance —
              worth {fmtMoney(pending.reduce((t, a) => t + Number(a.purchase_value ?? 0), 0))}.
              Register them with the broker, then mark them insured here.
            </div>
            <button type="button" onClick={() => setTab("pending")}
              className="text-[12px] font-mono text-warn hover:underline shrink-0">show them →</button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPI label="Assets"            value={assets.length} sub="on the register" />
        <KPI label="Total value"       value={fmtMoney(totalValue)} sub="purchase value" />
        <KPI label="Insured"           value={fmtMoney(insuredValue)} sub="confirmed with broker" />
        <KPI label="Awaiting insurance" value={pending.length} sub="outstanding" accent={pending.length > 0} />
      </div>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md">
          {(["all", "pending", "insured"] as const).map(t => (
            <button key={t} type="button" onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded text-[12px] capitalize transition-colors ${
                tab === t ? "bg-accent/15 text-accent border border-accent/30" : "text-text-muted hover:text-text"}`}>
              {t === "pending" ? "awaiting insurance" : t}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-dim" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search assets…"
            className="w-full bg-ink-card border border-line rounded-md pl-8 pr-3 py-1.5 text-[13px] text-text placeholder:text-text-dim" />
        </div>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center"><Spinner /></div>
      ) : shown.length === 0 ? (
        <EmptyState icon={Package} title="Nothing here yet"
          description={`Approve a CapEx tool or equipment purchase of $${threshold.toLocaleString()}+ in AP coding and it'll offer itself here — or add one manually.`} />
      ) : (
        <Card>
          <div className="divide-y divide-line">
            {shown.map(a => (
              <div key={a.asset_id} className="p-4 flex items-start gap-4 hover:bg-ink-raised/30 cursor-pointer"
                   onClick={() => setEditing(a)}>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-text">{a.description}</div>
                  <div className="text-[11.5px] text-text-muted mt-0.5">
                    {[
                      a.purchase_date ? format(parseISO(a.purchase_date), "d MMM yyyy") : null,
                      a.supplier_name,
                      a.budget_code,
                      a.receipt_ref ? `inv ${a.receipt_ref}` : null,
                      a.serial_number ? `s/n ${a.serial_number}` : null,
                      a.location,
                    ].filter(Boolean).join(" · ")}
                  </div>
                  {a.notes && <div className="text-[11.5px] text-text-dim mt-1">{a.notes}</div>}
                </div>
                <div className="text-[14px] font-mono text-text shrink-0">{fmtMoney(a.purchase_value)}</div>
                <div className="shrink-0 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {a.insurance_status === "pending" && a.insurance_required && (
                    <>
                      <Pill label="awaiting insurance" tone="warning" small />
                      <Button variant="secondary" size="sm" icon={ShieldCheck}
                        onClick={() => void setStatus(a, "insured")}
                        title="Confirmed with the broker — stamps today's date">
                        Mark insured
                      </Button>
                    </>
                  )}
                  {a.insurance_status === "insured" && (
                    <Pill label={`insured${a.insurance_confirmed_on ? ` ${format(parseISO(a.insurance_confirmed_on), "d MMM yy")}` : ""}`} tone="approved" small />
                  )}
                  {a.insurance_status === "not_required" && (
                    <Pill label="no insurance needed" tone="default" small />
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {(adding || editing) && (
        <AssetModal
          asset={editing}
          userId={(user as any).user_id ?? null}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => {
            setAdding(false); setEditing(null);
            qc.invalidateQueries({ queryKey: ["company_assets"] });
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function AssetModal({ asset, userId, onClose, onSaved }: {
  asset: AssetRow | null;
  userId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [desc, setDesc] = useState(asset?.description ?? "");
  const [date, setDate] = useState(asset?.purchase_date ?? "");
  const [value, setValue] = useState(asset?.purchase_value != null ? String(asset.purchase_value) : "");
  const [supplier, setSupplier] = useState(asset?.supplier_name ?? "");
  const [ref, setRef] = useState(asset?.receipt_ref ?? "");
  const [code, setCode] = useState(asset?.budget_code ?? "");
  const [serial, setSerial] = useState(asset?.serial_number ?? "");
  const [location, setLocation] = useState(asset?.location ?? "");
  const [insReq, setInsReq] = useState(asset?.insurance_required ?? true);
  const [status, setStatus] = useState<AssetRow["insurance_status"]>(asset?.insurance_status ?? "pending");
  const [notes, setNotes] = useState(asset?.notes ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    if (!desc.trim()) { setErr("Description is required."); return; }
    setBusy(true); setErr(null);
    const row: any = {
      description: desc.trim(),
      purchase_date: date || null,
      purchase_value: value === "" ? null : Number(value),
      supplier_name: supplier.trim() || null,
      receipt_ref: ref.trim() || null,
      budget_code: code.trim() || null,
      serial_number: serial.trim() || null,
      location: location.trim() || null,
      insurance_required: insReq,
      insurance_status: !insReq ? "not_required" : status === "not_required" ? "pending" : status,
      insurance_confirmed_on: status === "insured" ? (asset?.insurance_confirmed_on ?? new Date().toISOString().slice(0, 10)) : null,
      notes: notes.trim() || null,
      updated_by: userId,
    };
    const res = asset
      ? await (supabase as any).from("company_assets").update(row).eq("asset_id", asset.asset_id)
      : await (supabase as any).from("company_assets").insert({ ...row, created_by: userId });
    setBusy(false);
    if (res.error) { setErr(res.error.message); return; }
    onSaved();
  }

  const input = "mt-1 w-full bg-ink-raised border border-line rounded-md px-3 py-2 text-[13px] text-text placeholder:text-text-dim";
  const lbl = "text-[11px] uppercase tracking-wider2 text-text-muted font-mono";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-ink-card border border-line rounded-xl w-full max-w-lg mb-10 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-line">
          <div className="text-[14px] font-semibold text-text">{asset ? "Edit asset" : "Add asset"}</div>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-hover text-text-muted"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block"><span className={lbl}>Description</span>
            <input value={desc} onChange={(e) => setDesc(e.target.value)} className={input} placeholder="e.g. Hilti TE 3000 breaker" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={lbl}>Purchase date</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} /></label>
            <label className="block"><span className={lbl}>Value ($ inc GST)</span>
              <input type="number" value={value} onChange={(e) => setValue(e.target.value)} className={input} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={lbl}>Supplier</span>
              <input value={supplier} onChange={(e) => setSupplier(e.target.value)} className={input} /></label>
            <label className="block"><span className={lbl}>Invoice / receipt ref</span>
              <input value={ref} onChange={(e) => setRef(e.target.value)} className={input} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={lbl}>CapEx code</span>
              <input value={code} onChange={(e) => setCode(e.target.value)} className={input} placeholder="CX…" /></label>
            <label className="block"><span className={lbl}>Serial number</span>
              <input value={serial} onChange={(e) => setSerial(e.target.value)} className={input} /></label>
          </div>
          <label className="block"><span className={lbl}>Assigned vehicle / location</span>
            <input value={location} onChange={(e) => setLocation(e.target.value)} className={input} placeholder="e.g. Hilux 402 · depot store" /></label>
          <label className="flex items-center gap-2 text-[13px] text-text">
            <input type="checkbox" className="accent-accent" checked={insReq} onChange={(e) => setInsReq(e.target.checked)} />
            Needs individual insurance
          </label>
          {insReq && (
            <label className="block"><span className={lbl}>Insurance status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as any)} className={input}>
                <option value="pending">Pending — with broker / to do</option>
                <option value="insured">Insured — confirmed</option>
              </select></label>
          )}
          <label className="block"><span className={lbl}>Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={input} /></label>
          {err && <div className="text-[12px] text-danger">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-line">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" icon={CheckCircle2} onClick={() => void save()} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}
